import React, { useContext, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useStore } from "../../store"
import { langContext } from "../../App"
import { langFriends } from "./friendsTranslate"
import { BACKEND_URL, ALERT_MESSAGE_TYPE_DANGER } from "../../const/Constants"
import AlertComponent from "../../utils/Alert"
import userAvatar from "../../assets/images/user_avatar.png"
import CustomModal from "../modal/CustomModal"
import { displayName, nicknameLabel } from "../../utils/displayName"



const PersonRow = ({ user, children }) => (
    <li className="flex items-center justify-between gap-3 py-2 border-b border-gray-100">
        <div className="flex items-center gap-3 min-w-0">
            <img src={user.image ? `${BACKEND_URL}${user.image}` : userAvatar} alt=""
                 className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
            <div className="min-w-0">
                <p className="fontstyles truncate">{displayName(user)}</p>
                {user.nickname && displayName(user) !== nicknameLabel(user) && <p className="text-xs text-gray-500 truncate">{nicknameLabel(user)}</p>}
            </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">{children}</div>
    </li>
)

const Button = ({ onClick, children, variant = "primary", disabled }) => (
    <button type="button" onClick={onClick} disabled={disabled}
            className={`px-3 py-1 rounded-lg text-sm disabled:opacity-50 ${variant === "primary"
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
        {children}
    </button>
)

const FriendsModal = observer(({ isOpen, onClose }) => {
    const lang = useContext(langContext)
    const t = langFriends[lang] || langFriends.eng
    const { friendsStore, usersStore } = useStore()

    const [tab, setTab] = useState("find")
    const [query, setQuery] = useState("")
    const [busy, setBusy] = useState(null)
    const [confirmRemove, setConfirmRemove] = useState(null)

    useEffect(() => {
        if (isOpen) {
            friendsStore.loadRequests()
            setTab(friendsStore.incomingCount ? "requests" : "find")
        } else {
            setQuery("")
            setConfirmRemove(null)
            friendsStore.clearSearch()
        }
    }, [isOpen])

    useEffect(() => {
        const q = query.trim()
        if (q.length < 3) {
            friendsStore.clearSearch()
            return
        }
        const timer = setTimeout(() => friendsStore.search(q), 400)
        return () => clearTimeout(timer)
    }, [query])

    const run = async (key, action) => {
        setBusy(key)
        const result = await action()
        setBusy(null)
        if (result && !result.status) {
            AlertComponent(ALERT_MESSAGE_TYPE_DANGER, result.message)
        }
    }

    const tabs = [
        ["find", t.tr_find],
        ["requests", `${t.tr_requests}${friendsStore.incomingCount ? ` (${friendsStore.incomingCount})` : ""}`],
        ["sent", t.tr_sent],
        ["friends", t.tr_friends],
    ]

    const searchAction = (user) => {
        switch (user.relation) {
            case "friends":
                return <span className="text-sm text-green-600">{t.tr_alreadyFriends}</span>
            case "outgoing":
                return <span className="text-sm text-gray-500">{t.tr_requestSent}</span>
            case "incoming":
                return <>
                    <Button disabled={busy === user._id}
                            onClick={() => run(user._id, () => friendsStore.respond(user.request_id, true)
                                .then(r => { r.status && friendsStore.setRelation(user._id, "friends"); return r }))}>
                        {t.tr_accept}
                    </Button>
                </>
            default:
                return <Button disabled={busy === user._id} onClick={() => run(user._id, () => friendsStore.sendRequest(user._id))}>
                    {t.tr_addFriend}
                </Button>
        }
    }

    const empty = (text) => <p className="text-center text-gray-500 py-6">{text}</p>

    return (
        <CustomModal isOpen={isOpen} isClose={onClose}>
            <div style={{ width: "min(480px, calc(100vw - 64px))" }} className="flex flex-col">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="fontstyles text-lg font-semibold">{t.tr_friends}</h2>
                    <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none" aria-label={t.tr_close}>×</button>
                </div>

                <div className="flex mb-3 border-b border-gray-200">
                    {tabs.map(([key, label]) => (
                        <button key={key} type="button" onClick={() => setTab(key)}
                                className={`flex-1 min-w-0 px-1 py-2 text-sm truncate ${tab === key
                                    ? "border-b-2 border-blue-500 text-blue-600"
                                    : "text-gray-600"}`}>
                            {label}
                        </button>
                    ))}
                </div>

                <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
                    {tab === "find" && (
                        <>
                            <input type="search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                                   placeholder={t.tr_searchPlaceholder}
                                   className="w-full p-2 mb-1 rounded-lg border border-gray-300 focus:outline-none focus:border-blue-500" />
                            <p className="text-xs text-gray-500 mb-2">{t.tr_searchHint}</p>
                            <ul>
                                {friendsStore.searchResults.map(user => (
                                    <PersonRow key={user._id} user={user}>{searchAction(user)}</PersonRow>
                                ))}
                            </ul>
                            {query.trim().length >= 3 && !friendsStore.searching && !friendsStore.searchResults.length && empty(t.tr_noResults)}
                        </>
                    )}

                    {tab === "requests" && (
                        friendsStore.incoming.length ? (
                            <ul>
                                {friendsStore.incoming.map(request => (
                                    <PersonRow key={request._id} user={request.user}>
                                        <Button disabled={busy === request._id}
                                                onClick={() => run(request._id, () => friendsStore.respond(request._id, true))}>
                                            {t.tr_accept}
                                        </Button>
                                        <Button variant="secondary" disabled={busy === request._id}
                                                onClick={() => run(request._id, () => friendsStore.respond(request._id, false))}>
                                            {t.tr_decline}
                                        </Button>
                                    </PersonRow>
                                ))}
                            </ul>
                        ) : empty(t.tr_noRequests)
                    )}

                    {tab === "sent" && (
                        friendsStore.outgoing.length ? (
                            <ul>
                                {friendsStore.outgoing.map(request => (
                                    <PersonRow key={request._id} user={request.user}>
                                        <Button variant="secondary" disabled={busy === request._id}
                                                onClick={() => run(request._id, () => friendsStore.cancel(request._id))}>
                                            {t.tr_cancel}
                                        </Button>
                                    </PersonRow>
                                ))}
                            </ul>
                        ) : empty(t.tr_noRequests)
                    )}

                    {tab === "friends" && (
                        usersStore.users.length ? (
                            <ul>
                                {usersStore.users.map(user => (
                                    <PersonRow key={user._id} user={user}>
                                        {confirmRemove === user._id ? (
                                            <>
                                                <Button disabled={busy === user._id}
                                                        onClick={() => run(user._id, () => friendsStore.remove(user._id)).then(() => setConfirmRemove(null))}>
                                                    {t.tr_confirmRemove}
                                                </Button>
                                                <Button variant="secondary" onClick={() => setConfirmRemove(null)}>{t.tr_cancel}</Button>
                                            </>
                                        ) : (
                                            <Button variant="secondary" onClick={() => setConfirmRemove(user._id)}>{t.tr_remove}</Button>
                                        )}
                                    </PersonRow>
                                ))}
                            </ul>
                        ) : empty(t.tr_noFriends)
                    )}
                </div>
            </div>
        </CustomModal>
    )
})

export default FriendsModal
